import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    TablePagination,
    Paper,
    Box,
    TextField,
    InputAdornment,
    CircularProgress,
    Chip,
    TableSortLabel,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Autocomplete,
    Stack,
    Divider,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FilterListIcon from "@mui/icons-material/FilterList";
import RefreshIcon from "@mui/icons-material/Refresh";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import Navbar from "../../../../globals/components/Navbar";
import { apiClient } from "../../../../api/httpClient";
import TablePaginationActions from "../../../../globals/components/TablePaginationActions";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

function LawFirmUserList() {
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [nameFilter, setNameFilter] = useState("");
    const [emailFilter, setEmailFilter] = useState("");
    const [orderBy, setOrderBy] = useState("created_at");
    const [order, setOrder] = useState("desc");
    const [filterDialogOpen, setFilterDialogOpen] = useState(false);
    const [lawFirms, setLawFirms] = useState([]);
    const [advancedFilters, setAdvancedFilters] = useState({
        lawFirm: [],
        position: [],
        status: [],
        startDate: null,
        endDate: null,
    });

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (nameFilter) params.name = nameFilter;
            if (emailFilter) params.email = emailFilter;

            const response = await apiClient("/list-lawfirm-users/", "GET", {}, params);
            setUsers(response);
        } catch (error) {
            console.error("Failed to fetch law firm users:", error);
        } finally {
            setLoading(false);
        }
    }, [nameFilter, emailFilter]);

    const fetchLawFirms = async () => {
        try {
            const response = await apiClient("/list-lawfirms/", "GET");
            setLawFirms(response.map(f => ({ label: f.name, value: f.lawfirm_id })));
        } catch (error) {
            console.error("Failed to fetch law firms:", error);
        }
    };

    useEffect(() => {
        fetchLawFirms();
    }, []);

    useEffect(() => {
        setPage(0); // Reset to first page when search changes so results aren't hidden
        const timer = setTimeout(() => {
            fetchUsers();
        }, 500); // Debounce search

        return () => clearTimeout(timer);
    }, [fetchUsers]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleRowClick = (user) => {
        if (user.is_paralegal) {
            navigate(`/vermost_paralegal_detail/${user.lawfirm_user_id}`);
        } else if (user.lawfirm_user_id && user.location_id) {
            navigate(`/editlawfirmuser/${user.lawfirm_user_id}/${user.location_id}`);
        } else if (user.lawfirm_id) {
            navigate(`/lawfirmdetails/${user.lawfirm_id}`);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        return dayjs(dateString).format("MM/DD/YYYY, h:mm:ss A");
    };

    const getStatusColor = (status) => {
        switch (status) {
            case "Accepted":
                return "success";
            case "Pending":
                return "warning";
            default:
                return "default";
        }
    };

    const getSortableValue = (user, column) => {
        switch (column) {
            case "name":
                return (user.name || "").toLowerCase();
            case "email":
                return (user.email || "").toLowerCase();
            case "law_firm":
                return (user.law_firm_name || "").toLowerCase();
            case "position":
                const pos = (user.position || "").toLowerCase();
                if (pos === "paralegal") {
                    return user.is_legal_service ? "legal service paralegal" : "paralegal";
                }
                if (pos === "admin") return "clerical staff";
                return pos;
            case "invite_status":
                return (user.invite_status || "").toLowerCase();
            case "created_at":
                return user.created_at ? new Date(user.created_at).getTime() : 0;
            default:
                return "";
        }
    };

    const handleSort = (column) => {
        const isAsc = orderBy === column && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(column);
    };

    const filteredUsers = users.filter((user) => {
        const { lawFirm, position, status, startDate, endDate } = advancedFilters;

        if (lawFirm.length > 0 && !lawFirm.some(f => f.label === user.law_firm_name)) return false;
        if (position.length > 0) {
            const rawPos = (user.position || "").toLowerCase();
            let userDisplayPosition = user.position || "";

            if (rawPos === "paralegal") {
                userDisplayPosition = user.is_legal_service ? "Legal Service Paralegal" : "Paralegal";
            } else if (rawPos === "admin") {
                userDisplayPosition = "Clerical Staff";
            } else if (rawPos === "attorney") {
                userDisplayPosition = "Attorney";
            }
            if (!position.includes(userDisplayPosition)) return false;
        }
        if (status.length > 0) {
            const userStatus = user.is_active ? "Active" : "Inactive";
            if (!status.includes(userStatus)) return false;
        }
        if (startDate) {
            const start = dayjs(startDate).startOf("day");
            if (!dayjs(user.created_at).isSameOrAfter(start, "day")) return false;
        }
        if (endDate) {
            const end = dayjs(endDate).endOf("day");
            if (!dayjs(user.created_at).isSameOrBefore(end, "day")) return false;
        }
        return true;
    });

    const sortedUsers = [...filteredUsers].sort((a, b) => {
        const aVal = getSortableValue(a, orderBy);
        const bVal = getSortableValue(b, orderBy);

        if (aVal < bVal) return order === "asc" ? -1 : 1;
        if (aVal > bVal) return order === "asc" ? 1 : -1;
        return 0;
    });

    return (
        <>
            <Navbar />

            <Box sx={{ maxWidth: "100%", padding: "20px" }}>
                <Typography variant="h4" sx={{ fontWeight: "bold", mb: 2 }}>
                    Law Firm Users
                </Typography>

                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 2,
                        marginBottom: "20px",
                    }}
                >
                    <Box sx={{ display: "flex", gap: 2 }}>
                        <TextField
                            label="Search by Name"
                            variant="outlined"
                            value={nameFilter}
                            onChange={(e) => setNameFilter(e.target.value)}
                            sx={{
                                width: { xs: "100%", md: "300px" },
                                borderRadius: "8px",
                            }}
                            slotProps={{ input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}}
                        />
                        <TextField
                            label="Search by Email"
                            variant="outlined"
                            value={emailFilter}
                            onChange={(e) => setEmailFilter(e.target.value)}
                            sx={{
                                width: { xs: "100%", md: "300px" },
                                borderRadius: "8px",
                            }}
                            slotProps={{ input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}}
                        />
                    </Box>

                    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                        <Button
                            startIcon={<RefreshIcon />}
                            onClick={() => fetchUsers()}
                            sx={{ color: "#1976d2", fontWeight: "bold", textTransform: "uppercase" }}
                        >
                            Refresh
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<FilterListIcon />}
                            onClick={() => setFilterDialogOpen(true)}
                            sx={{
                                backgroundColor: "#1976d2",
                                borderRadius: "8px",
                                padding: "8px 24px",
                                fontWeight: "bold",
                                "&:hover": { backgroundColor: "#1565c0" }
                            }}
                        >
                            Filters
                        </Button>
                    </Box>
                </Box>

                {loading ? (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        height="50vh"
                    >
                        <CircularProgress />
                    </Box>
                ) : (
                    <TableContainer
                        component={Paper}
                        sx={{
                            width: "100%",
                            overflowX: "auto",
                            mb: 2,
                            borderRadius: "8px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        }}
                    >
                        <Table>
                            <TableHead>
                                <TableRow style={{ backgroundColor: "#f8f9fa" }}>
                                    <TableCell sx={{ fontWeight: "bold" }}>
                                        <TableSortLabel
                                            active={orderBy === "name"}
                                            direction={orderBy === "name" ? order : "asc"}
                                            onClick={() => handleSort("name")}
                                        >
                                            Name
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: "bold" }}>
                                        <TableSortLabel
                                            active={orderBy === "email"}
                                            direction={orderBy === "email" ? order : "asc"}
                                            onClick={() => handleSort("email")}
                                        >
                                            Email
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: "bold" }}>
                                        <TableSortLabel
                                            active={orderBy === "law_firm"}
                                            direction={orderBy === "law_firm" ? order : "asc"}
                                            onClick={() => handleSort("law_firm")}
                                        >
                                            Law Firm
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: "bold" }}>
                                        <TableSortLabel
                                            active={orderBy === "position"}
                                            direction={orderBy === "position" ? order : "asc"}
                                            onClick={() => handleSort("position")}
                                        >
                                            Position
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: "bold" }}>
                                        <TableSortLabel
                                            active={orderBy === "invite_status"}
                                            direction={orderBy === "invite_status" ? order : "asc"}
                                            onClick={() => handleSort("invite_status")}
                                        >
                                            Invite Status
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: "bold" }}>
                                        <TableSortLabel
                                            active={orderBy === "created_at"}
                                            direction={orderBy === "created_at" ? order : "asc"}
                                            onClick={() => handleSort("created_at")}
                                        >
                                            Date Joined
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: "bold" }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sortedUsers
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((user) => (
                                        <TableRow
                                            key={user.lawfirm_user_id}
                                            hover
                                        >
                                            <TableCell>{user.name || "N/A"}</TableCell>
                                            <TableCell>{user.email || "N/A"}</TableCell>
                                            <TableCell>{user.law_firm_name || "N/A"}</TableCell>
                                            <TableCell>
                                                {(() => {
                                                    const pos = (user.position || "").toLowerCase();
                                                    if (pos === "paralegal") {
                                                        return user.is_legal_service ? "Legal Service Paralegal" : "Paralegal";
                                                    }
                                                    if (pos === "admin") {
                                                        return "Clerical Staff";
                                                    }
                                                    return user.position || "N/A";
                                                })()}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={user.invite_status}
                                                    color={getStatusColor(user.invite_status)}
                                                    size="small"
                                                    sx={{ fontWeight: "medium" }}
                                                />
                                            </TableCell>
                                            <TableCell>{formatDate(user.created_at)}</TableCell>
                                            <TableCell>
                                                <VisibilityIcon
                                                    sx={{
                                                        color: "gray",
                                                        cursor: "pointer",
                                                        "&:hover": { color: "black" },
                                                    }}
                                                    onClick={() => handleRowClick(user)}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                {users.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                                            No law firm users found.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                <TablePagination
          ActionsComponent={TablePaginationActions}
                    component="div"
                    count={sortedUsers.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 25, 50]}
                />

                <Dialog
                    open={filterDialogOpen}
                    onClose={() => setFilterDialogOpen(false)}
                    maxWidth="sm"
                    fullWidth
                    slotProps={{ paper: { sx: { borderRadius: "12px" } }}}
                >
                    <DialogTitle sx={{ fontWeight: "bold", pb: 1 }}>Apply Filters</DialogTitle>
                    <Divider />
                    <DialogContent sx={{ pt: 3 }}>
                        <Stack spacing={3}>
                            <Autocomplete
                                multiple
                                options={lawFirms}
                                value={advancedFilters.lawFirm}
                                onChange={(e, newVal) => setAdvancedFilters(prev => ({ ...prev, lawFirm: newVal }))}
                                renderInput={(params) => <TextField {...params} label="Law Firm" variant="outlined" />}
                            />
                            <Autocomplete
                                multiple
                                options={["Attorney", "Paralegal", "Legal Service Paralegal", "Clerical Staff"]}
                                value={advancedFilters.position}
                                onChange={(e, newVal) => setAdvancedFilters(prev => ({ ...prev, position: newVal }))}
                                renderInput={(params) => <TextField {...params} label="Position" variant="outlined" />}
                            />
                            <Autocomplete
                                multiple
                                options={["Active", "Inactive"]}
                                value={advancedFilters.status}
                                onChange={(e, newVal) => setAdvancedFilters(prev => ({ ...prev, status: newVal }))}
                                renderInput={(params) => <TextField {...params} label="Status" variant="outlined" />}
                            />
                            <Box sx={{ display: "flex", gap: 2 }}>
                                <TextField
                                    label="Start Date Joined"
                                    type="date"
                                    fullWidth
                                    value={advancedFilters.startDate ? dayjs(advancedFilters.startDate).format("YYYY-MM-DD") : ""}
                                    onChange={(e) => setAdvancedFilters(prev => ({ ...prev, startDate: e.target.value ? dayjs(e.target.value) : null }))}
                                    slotProps={{ inputLabel: { shrink: true }}}
                                />
                                <TextField
                                    label="End Date Joined"
                                    type="date"
                                    fullWidth
                                    value={advancedFilters.endDate ? dayjs(advancedFilters.endDate).format("YYYY-MM-DD") : ""}
                                    onChange={(e) => setAdvancedFilters(prev => ({ ...prev, endDate: e.target.value ? dayjs(e.target.value) : null }))}
                                    slotProps={{ inputLabel: { shrink: true }}}
                                />
                            </Box>
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ p: 3, gap: 1 }}>
                        <Button
                            onClick={() => {
                                setAdvancedFilters({ lawFirm: [], position: [], status: [], startDate: null, endDate: null });
                            }}
                            sx={{ color: "#1976d2", fontWeight: "bold" }}
                        >
                            CLEAR
                        </Button>
                        <Button onClick={() => setFilterDialogOpen(false)} sx={{ color: "#1976d2", fontWeight: "bold" }}>
                            CANCEL
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => setFilterDialogOpen(false)}
                            sx={{ backgroundColor: "#1976d2", borderRadius: "8px", fontWeight: "bold", px: 4 }}
                        >
                            APPLY
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </>
    );
}

export default LawFirmUserList;
